<script lang="ts">
    import { setContext } from 'svelte'
    import { derived as sderived, writable, type Writable } from 'svelte/store'

    // import type { DateTime } from 'luxon'
    import * as yup from 'yup'

    import type { CrumbArray } from 'hydria/components/breadcrumb'
    import { validateSyncAt } from 'hydria/document_helpers'
    import { HydriaDocument, type IDocument } from 'hydria/HydriaDocument'
    import type { IRevision } from 'hydria/HydriaDocument'
    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { spath } from 'hydria/svelte_helpers'

    import { Breadcrumb } from 'breadcrumb'

    import DocumentEdit from 'hydria/components/DocumentEdit.svelte'
    // import GenDateTimeInput from 'hydria/components/Generics/GenDateTimeInput.svelte'
    import GenTextInput from 'hydria/components/Generics/GenTextInput.svelte'
    import LanguageSelector from 'hydria/components/Generics/LanguageSelector.svelte'
    import IntroHtml from 'hydria/field_components/IntroHtml.svelte'

    interface Props {
        route: any
    }

    let { route }: Props = $props()
    // const revFields = ['title', 'content_html', 'lg', 'displayed_time']
    const revFields = ['title', 'content_html', 'lg']

    interface ILocalDocument extends IDocument {}
    interface ILocalRevision extends IRevision {
        title: string
        content_html: string
        // displayed_time: DateTime
    }

    class LocalDocument extends HydriaDocument {
        docType = 'nouvelle_client'
        docTypePlural = 'nouvelles_client'

        docTypeName = tgettext('Nouvelle Client')
        docTypeNamePlural = tgettext('Nouvelles Clients')
    }

    const Document = new LocalDocument()
    const status = sderived(Document.status, ($status) => $status)

    let inDocId = $derived(route.result.path.params?.docId)
    let inRevId = $derived(route.result.path.params?.revId)

    const revSaved = sderived(Document.revision, ($rev) => (!$rev ? $rev : Object.fromEntries(Object.entries($rev).filter(([k, v]) => revFields.includes(k)))) as ILocalRevision)
    const docSaved = sderived(Document.document, ($rev) => ({ ...$rev }) as ILocalDocument)

    // revEdit is actually handled in DocumentEdit.
    let revEdit: Writable<ILocalRevision | undefined> = writable(undefined)
    setContext('revEdit', revEdit)

    const revSchema = yup.object().shape({
        title: yup.string().required(),
        content_html: yup.string().required(),
        // displayed_time: yup.date().required(),
        lg: yup
            .string()
            .required()
            .test('lgIsValid', '${path} is not valid', (value) => value === 'fr' || value === 'en'),
    })
    let formIsValid = $derived(revSchema.isValidSync($revEdit))

    const crumbs = sderived(status, ($status) => {
        const c: CrumbArray = [{ txt: Document.docTypeNamePlural, url: spath(`/${Document.docTypePlural.replace('_', '-')}`) }]
        switch ($status) {
            case 'loading':
                c.push({
                    txt: '…',
                })
                break
            case 'error':
            case 'not-found':
                c.push({
                    icon: 'mdi:alert',
                })
                break
            case 'ready':
                c.push({
                    txt: tgettext('Edit'),
                })
                c.push({
                    txt: `<span class="text-muted">${Document.docId}</span>`,
                    html: true,
                })
        }
        return c
    })
</script>

<Breadcrumb {crumbs} />

<DocumentEdit {Document} docId={inDocId} revId={inRevId} {formIsValid} {revFields}>
    {#if $revEdit}
        <div class="card card-content">
            <div class="card-header text-bg-primary">{$_('Document')}</div>
            <div class="card-body d-grid gap-2">
                <GenTextInput label={tgettext('Titre')} saved={$revSaved.title} bind:value={$revEdit.title} isValid={(v) => validateSyncAt(revSchema, 'title', { title: v })} helpText={tgettext('Ce texte est utilisé pour le titre de la page, dans la barre du navigateur.')} disabled={$status != 'ready'} />

                <LanguageSelector bind:value={$revEdit.lg} />

                <!-- <GenDateTimeInput label={tgettext('Date affichée')} saved={$revSaved.displayed_time} bind:valur={$revEdit.displayed_time} isValid={(v) => validateSyncAt(revSchema, 'displayed_time', { displayed_time: v })} disabled={$status != 'ready'} /> -->

                <IntroHtml saved={$revSaved.content_html} bind:value={$revEdit.content_html} disabled={$status != 'ready'} label={tgettext('Contenu')} helpText={undefined} isValid={(v) => validateSyncAt(revSchema, 'content_html', { content_html: v })} />
            </div>
        </div>
    {/if}
</DocumentEdit>
